import { queryOptions } from '@tanstack/react-query'
import type { components } from '@/api/schema'
import type { QuoteBuilderApi } from '../api/client'
import type { Address, Quote } from '../api/types'

// Customer stage reads and saves (USA-1023), ported from the demo's customer-info-api.ts.

type Schemas = components['schemas']
export type CustomerIdentity = Schemas['QbQuoteCustomerIdentity']
export type QuoteContact = Schemas['QbQuoteContactRead']
export type CustomerInfoUpdate = Omit<Schemas['QbQuoteCustomerInfoUpdate'], 'expected_revision'>
export type AddressCheck = Schemas['QbAddressCheckRead']

/** The quote's company and contact as the CRM has them now, with the contacts to choose from. */
export const customerIdentityQueryOptions = (api: QuoteBuilderApi, quote: Pick<Quote, 'id' | 'customer_id' | 'contact_id'>) =>
  queryOptions({
    queryKey: ['quote-customer-info', quote.id, quote.customer_id, quote.contact_id],
    queryFn: ({ signal }) => api.request<CustomerIdentity | null>(`/quotes/${quote.id}/customer-info`, { signal }),
  })

const addressKey = (address: Address) =>
  [address.line1, address.line2, address.city, address.state, address.postal_code, address.country].map((part) => part.trim().toLowerCase()).join('|')

export const addressComplete = (address: Address) =>
  [address.line1, address.city, address.state, address.postal_code].every((part) => part.trim())

/** Google's verdict on one saved address; checked once per address. */
export const addressCheckQueryOptions = (api: QuoteBuilderApi, quoteId: string, address: Address) =>
  queryOptions({
    queryKey: ['address-check', addressKey(address)],
    queryFn: ({ signal }) =>
      api.request<AddressCheck>(`/quotes/${quoteId}/customer-info/address-check`, {
        method: 'POST',
        body: JSON.stringify({ address }),
        signal,
      }),
    staleTime: Infinity,
    enabled: addressComplete(address),
  })

export const customerHistoryQueryOptions = (api: QuoteBuilderApi, customerId: number) =>
  queryOptions({
    queryKey: ['customer-history', customerId],
    queryFn: ({ signal }) => api.request<Quote[]>(`/quotes?customer_id=${customerId}`, { signal }),
    staleTime: 30_000,
  })
